import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { resolveHeader } from './siteHeader';
import { resolveSiteSeo } from './siteSeo';
import { resolveGallery, resolveGalleryAlbums } from './siteGallery';
import DocumentHead from './DocumentHead';
import SiteFooter from './components/SiteFooter';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';
import { apiUrl } from './apiBase';

const FALLBACK_HERO =
  'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1777804247/rafid-seed/admin/u2cu0nlo55bxxufhxrmf.jpg';

export default function GalleryPage() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));

  const gallerySection = useMemo(() => resolveGallery(site), [site.gallery]);
  const galleryAlbums = useMemo(() => resolveGalleryAlbums(site), [site.gallery]);
  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const baseSeo = useMemo(() => resolveSiteSeo(site), [site]);

  const pageSeo = useMemo(() => {
    const brand = `${headerSection.brandPrimary} ${headerSection.brandSecondary}`.trim() || 'Rafid Seed';
    return {
      ...baseSeo,
      htmlTitle: `Gallery | ${brand}`,
      socialTitle: `Gallery — ${baseSeo.socialTitle || brand}`,
      canonicalUrl: typeof window !== 'undefined' ? `${window.location.origin}/gallery` : '',
    };
  }, [baseSeo, headerSection.brandPrimary, headerSection.brandSecondary]);

  const heroImage =
    gallerySection.pageHeroImage?.trim() ||
    galleryAlbums[0]?.thumbnail ||
    FALLBACK_HERO;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => { });
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <DocumentHead seo={pageSeo} />

      <MarketingSiteHeader
        site={site}
        idPrefix="gallery"
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <section className="relative isolate min-h-[min(30vh,260px)] overflow-hidden pb-0 pt-[calc(3.85rem+env(safe-area-inset-top))] sm:min-h-[min(36vh,300px)] sm:pt-[calc(4.25rem+env(safe-area-inset-top))] md:pt-[calc(4.5rem+env(safe-area-inset-top))]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/55" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-forest/85 to-transparent" aria-hidden />
        <div className="relative z-[1] mx-auto mt-40 max-w-shell px-4">
          <div className="relative">
            <div className="relative overflow-hidden rounded-t-[60px] bg-[#e8f5e9] px-6 pb-10 pt-10 text-center shadow-[0_-20px_60px_rgba(0,0,0,0.15)]">
              <div className="absolute -left-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" />
              <div className="absolute -right-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" />
              <h1 className="text-3xl font-bold text-brand-forest">
                {gallerySection.sectionTitle || 'Gallery'}
              </h1>
              <p className="mt-3 flex justify-center gap-2 text-xs uppercase text-brand-lime">
                HOME :
                <span className="text-brand-forest">GALLERY</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="relative bg-white">
        <div className="mx-auto max-w-shell px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-12 lg:px-8">
          <p className="mx-auto max-w-3xl text-center text-base leading-relaxed text-neutral-600 sm:text-lg">
            {gallerySection.intro}
          </p>

          {galleryAlbums.length === 0 ? (
            <p className="text-center text-sm text-neutral-600">No albums to show yet.</p>
          ) : (
          <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
            {galleryAlbums.map(({ title, thumbnail, alt, itemCount }, index) => (
              <li key={title}>
                <Link
                  to={`/gallery/album/${encodeURIComponent(title)}`}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-white text-inherit no-underline shadow-sm ring-1 ring-neutral-200/90 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-olive"
                >
                  <div className="gallery-album-thumb relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-neutral-200/60">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={alt || title || ''}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out  motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        loading={index < 6 ? 'eager' : 'lazy'}
                        decoding="async"
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200 px-4 text-center">
                        <span className="text-sm font-semibold text-neutral-500">{title}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-neutral-100/90 bg-brand-cream/50 px-3 py-2.5 text-center sm:py-3">
                    <p className="m-0 text-sm font-semibold leading-snug text-brand-forest">{title}</p>
                    {/* {itemCount > 0 ? (
                      <p className="m-0 mt-1 text-xs text-neutral-500">
                        {itemCount} {itemCount === 1 ? 'photo' : 'photos'}
                      </p>
                    ) : null} */}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          )}
        </div>
      </main>

      <SiteFooter site={site} headerSection={headerSection} />
    </div>
  );
}
